import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/StatusBadge';
import { EmptyState } from '@/components/EmptyState';
import { Search, User, Building2, Calendar, Plane, MapPin } from 'lucide-react';
import { fmtDateTime, fmtDate } from '@/lib/dates';

export default function SearchResultsPage() {
  const [params, setParams] = useSearchParams();
  const initQ = params.get('q') || '';
  const [q, setQ] = useState(initQ);
  const [results, setResults] = useState({ pejabat: [], opd: [], agendas: [], sppds: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) {
      setResults({ pejabat: [], opd: [], agendas: [], sppds: [] });
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setParams(q ? { q } : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const hasResults = results.pejabat.length || results.opd.length || results.agendas.length || results.sppds.length;

  return (
    <div className="space-y-6">
      <PageHeader title="Pencarian Cepat" subtitle="Cari pejabat, OPD, agenda, atau SPPD secara terpusat." />

      <Card className="border-slate-200 shadow-premium">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-input" placeholder="Cari nama pejabat, OPD, agenda, SPPD…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11 text-base" autoFocus />
          </div>
        </CardContent>
      </Card>

      {q.length < 2 && <EmptyState title="Ketik untuk mulai mencari" description="Minimal 2 karakter." />}
      {q.length >= 2 && !loading && !hasResults && <EmptyState title="Tidak ada hasil" description={`Tidak ditemukan hasil untuk "${q}"`} />}

      {results.pejabat.length > 0 && (
        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold flex items-center gap-2"><User className="w-4 h-4 text-[#DC2626]" />Pejabat ({results.pejabat.length})</CardTitle></CardHeader>
          <CardContent className="divide-y divide-slate-100">
            {results.pejabat.map((p) => (
              <div key={p.id} className="py-3 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center font-bold flex-shrink-0">{p.nama[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{p.nama}</div>
                  <div className="text-xs text-slate-600">{p.jabatan} · {p.opd_nama}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    {p.status_dinas === 'Sedang Dinas'
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 font-semibold"><Plane className="w-3 h-3" />Sedang Dinas di {p.lokasi_dinas}</span>
                      : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold"><MapPin className="w-3 h-3" />Di Makassar</span>
                    }
                    <span className="text-slate-500">Agenda berikutnya: <strong>{p.agenda_berikutnya}</strong>{p.agenda_berikutnya_tanggal ? ` (${fmtDateTime(p.agenda_berikutnya_tanggal)})` : ''}</span>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {results.opd.length > 0 && (
        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold flex items-center gap-2"><Building2 className="w-4 h-4 text-[#DC2626]" />OPD ({results.opd.length})</CardTitle></CardHeader>
          <CardContent className="divide-y divide-slate-100">
            {results.opd.map((o) => (
              <div key={o.id} className="py-3">
                <div className="font-semibold">{o.nama}</div>
                <div className="text-xs text-slate-500">{o.kode} · {o.jenis} · {o.kepala}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {results.agendas.length > 0 && (
        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold flex items-center gap-2"><Calendar className="w-4 h-4 text-[#DC2626]" />Agenda ({results.agendas.length})</CardTitle></CardHeader>
          <CardContent className="divide-y divide-slate-100">
            {results.agendas.map((a) => (
              <Link key={a.id} to={`/agenda/${a.id}`} className="py-3 flex items-start gap-3 hover:bg-slate-50 -mx-3 px-3 rounded">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{a.nama_acara}</div>
                  <div className="text-xs text-slate-500">{fmtDateTime(a.tanggal_mulai)} · {a.lokasi}</div>
                </div>
                <StatusBadge status={a.status} type="agenda" />
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      {results.sppds.length > 0 && (
        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold flex items-center gap-2"><Plane className="w-4 h-4 text-[#DC2626]" />SPPD ({results.sppds.length})</CardTitle></CardHeader>
          <CardContent className="divide-y divide-slate-100">
            {results.sppds.map((s) => (
              <Link key={s.id} to={`/sppd/${s.id}`} className="py-3 flex items-start gap-3 hover:bg-slate-50 -mx-3 px-3 rounded">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold font-mono text-sm">{s.nomor_sppd}</div>
                  <div className="text-xs text-slate-600">{s.pejabat_nama} → {s.tujuan_kota}</div>
                  <div className="text-xs text-slate-500">{fmtDate(s.tanggal_berangkat)} - {fmtDate(s.tanggal_kembali)}</div>
                </div>
                <StatusBadge status={s.status} type="sppd" />
              </Link>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
